import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Plus, Search, Edit, Trash2, FileOutput, Filter, HelpCircle } from 'lucide-react';
import { documentTemplateService } from '../services/api';
import useNotification from '../hooks/useNotification';
import Notification from '../components/common/Notification';
import ConfirmDialog from '../components/common/ConfirmDialog';
import DocumentTemplateEditor from '../components/documents/DocumentTemplateEditor';
import GenerateDocumentModal from '../components/documents/GenerateDocumentModal';

const CATEGORIES = [
  'Family Law', 'Personal Injury', 'Criminal Defense', 'Real Estate',
  'Estate Planning', 'Business', 'Employment', 'Immigration', 'General'
];

const DocumentTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const debounceRef = useRef(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generateTemplate, setGenerateTemplate] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, template: null });
  const [deleting, setDeleting] = useState(false);
  const { notification, showSuccess, showError, hideNotification } = useNotification();

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (categoryFilter) params.category = categoryFilter;
      const res = await documentTemplateService.list(params);
      setTemplates(res.data.templates || []);
    } catch {
      showError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter, showError]);

  // Debounce search input (300ms) to avoid excessive API calls during typing
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(searchInput);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleDelete = async () => {
    if (!deleteConfirm.template) return;
    try {
      setDeleting(true);
      await documentTemplateService.delete(deleteConfirm.template.id);
      showSuccess('Template deleted');
      setDeleteConfirm({ open: false, template: null });
      fetchTemplates();
    } catch {
      showError('Failed to delete template');
    } finally {
      setDeleting(false);
    }
  };

  const handleEditorClose = (saved) => {
    setEditorOpen(false);
    setEditingTemplate(null);
    if (saved) {
      fetchTemplates();
    }
  };

  const handleGenerateClose = (generated) => {
    setGenerateOpen(false);
    setGenerateTemplate(null);
    if (generated) {
      showSuccess('Document generated and saved to case');
    }
  };

  return (
    <div>
      <Notification {...notification} onClose={hideNotification} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="h-6 w-6 text-blue-600" />
            Document Templates
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Reusable document templates with auto-fill from case and client data
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/document-templates/help"
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
          >
            <HelpCircle className="h-4 w-4" />
            How to Use
          </Link>
          <button
            onClick={() => { setEditingTemplate(null); setEditorOpen(true); }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            <Plus className="h-4 w-4" />
            New Template
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search templates by name or content..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
          >
            <option value="">All Categories</option>
            {CATEGORIES.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Template grid */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading templates...</div>
      ) : templates.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No templates yet</h3>
          <p className="text-sm text-gray-500 mb-4">
            Create your first document template to auto-generate legal documents with case data.
          </p>
          <button
            onClick={() => { setEditingTemplate(null); setEditorOpen(true); }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            <Plus className="h-4 w-4" />
            Create Template
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(template => (
            <div
              key={template.id}
              className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">{template.name}</h3>
                  {template.category && (
                    <span className="inline-block mt-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                      {template.category}
                    </span>
                  )}
                </div>
                <FileText className="h-5 w-5 text-gray-400 flex-shrink-0 ml-2" />
              </div>
              {template.description && (
                <p className="text-sm text-gray-500 mb-3 line-clamp-2">{template.description}</p>
              )}
              {template.practice_area && (
                <p className="text-xs text-gray-400 mb-3">Practice Area: {template.practice_area}</p>
              )}
              <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                <button
                  onClick={() => { setGenerateTemplate(template); setGenerateOpen(true); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 text-xs font-medium"
                  title="Generate document from this template"
                >
                  <FileOutput className="h-3.5 w-3.5" />
                  Generate
                </button>
                <button
                  onClick={() => { setEditingTemplate(template); setEditorOpen(true); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 text-xs font-medium"
                  title="Edit template"
                >
                  <Edit className="h-3.5 w-3.5" />
                  Edit
                </button>
                <button
                  onClick={() => setDeleteConfirm({ open: true, template })}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg text-xs font-medium ml-auto"
                  title="Delete template"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor modal */}
      {editorOpen && (
        <DocumentTemplateEditor
          template={editingTemplate}
          onClose={handleEditorClose}
          categories={CATEGORIES}
        />
      )}

      {/* Generate modal */}
      {generateOpen && generateTemplate && (
        <GenerateDocumentModal
          template={generateTemplate}
          onClose={handleGenerateClose}
        />
      )}

      {/* Delete confirmation */}
      <ConfirmDialog
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, template: null })}
        onConfirm={handleDelete}
        title="Delete Template"
        message={`Are you sure you want to delete "${deleteConfirm.template?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        loading={deleting}
      />
    </div>
  );
};

export default DocumentTemplates;
